/**
 * Favorites Service - localStorage-based favorites management
 * Provides CRUD operations for saving/removing favorite vessels and locations
 */

export interface FavoriteVessel {
  id: string;
  name: string;
  registreringsmerke: string;
  savedAt: string;
}

export interface FavoriteLocation {
  id: string;
  name: string;
  type: string;
  savedAt: string;
}

export interface FavoritesData {
  vessels: FavoriteVessel[];
  locations: FavoriteLocation[];
}

const STORAGE_KEY = 'fangstdata_favorites';

/**
 * Get all favorites from localStorage
 */
export function getFavorites(): FavoritesData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { vessels: [], locations: [] };
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading favorites from localStorage:', error);
    return { vessels: [], locations: [] };
  }
}

/**
 * Save favorites to localStorage
 */
function saveFavorites(favorites: FavoritesData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    console.error('Error saving favorites to localStorage:', error);
  }
}

/**
 * Add a vessel to favorites
 */
export function addVessel(vessel: FavoriteVessel): void {
  const favorites = getFavorites();

  // Check if already favorited
  if (favorites.vessels.some((v) => v.registreringsmerke === vessel.registreringsmerke)) {
    return;
  }

  vessel.savedAt = new Date().toISOString();
  favorites.vessels.unshift(vessel); // Add to beginning (newest first)
  saveFavorites(favorites);
}

/**
 * Add a location to favorites
 */
export function addLocation(location: FavoriteLocation): void {
  const favorites = getFavorites();

  // Check if already favorited
  if (favorites.locations.some((l) => l.id === location.id)) {
    return;
  }

  location.savedAt = new Date().toISOString();
  favorites.locations.unshift(location); // Add to beginning (newest first)
  saveFavorites(favorites);
}

/**
 * Remove a vessel from favorites by registreringsmerke
 */
export function removeVessel(registreringsmerke: string): void {
  const favorites = getFavorites();
  favorites.vessels = favorites.vessels.filter(
    (v) => v.registreringsmerke !== registreringsmerke
  );
  saveFavorites(favorites);
}

/**
 * Remove a location from favorites by ID
 */
export function removeLocation(locationId: string): void {
  const favorites = getFavorites();
  favorites.locations = favorites.locations.filter(
    (l) => l.id !== locationId
  );
  saveFavorites(favorites);
}

/**
 * Check if a vessel is favorited
 */
export function isVesselFavorited(registreringsmerke: string): boolean {
  const favorites = getFavorites();
  return favorites.vessels.some((v) => v.registreringsmerke === registreringsmerke);
}

/**
 * Check if a location is favorited
 */
export function isLocationFavorited(locationId: string): boolean {
  const favorites = getFavorites();
  return favorites.locations.some((l) => l.id === locationId);
}

/**
 * Toggle favorite status for a vessel
 * Returns true if added, false if removed
 */
export function toggleVessel(vessel: FavoriteVessel): boolean {
  if (isVesselFavorited(vessel.registreringsmerke)) {
    removeVessel(vessel.registreringsmerke);
    return false;
  } else {
    addVessel(vessel);
    return true;
  }
}

/**
 * Toggle favorite status for a location
 * Returns true if added, false if removed
 */
export function toggleLocation(location: FavoriteLocation): boolean {
  if (isLocationFavorited(location.id)) {
    removeLocation(location.id);
    return false;
  } else {
    addLocation(location);
    return true;
  }
}
